/**
 * PendingMeetings Component
 *
 * Displays a list of pending meeting requests (activities with type=MEETING, isCompleted=false).
 *
 * Requirements: Task 14 - Meeting Request Page
 */

import { Card, CardContent, CardHeader, CardTitle, Badge } from '@bloomcode/ui';
import { CalendarDays, Clock } from '@bloomcode/icons';
import type { GetMyActivitiesQuery } from '@bloomcode/graphql';

type ActivityItem = GetMyActivitiesQuery['getMyActivities']['data'][number];

interface PendingMeetingsProps {
  meetings: ActivityItem[];
  isLoading: boolean;
}

/**
 * Formats a date string to a readable format
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'No date set';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PendingMeetings({ meetings, isLoading }: PendingMeetingsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Pending Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse space-y-2 p-3 rounded-lg border border-border">
                <div className="h-4 w-40 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
                <div className="h-3 w-24 rounded bg-muted" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (meetings.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Pending Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CalendarDays className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No pending meeting requests.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Pending Meetings
          <Badge variant="secondary" className="ml-auto">
            {meetings.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3" role="list" aria-label="Pending meeting requests">
          {meetings.map((meeting) => (
            <div
              key={meeting.id}
              className="p-3 rounded-lg border border-border hover:border-primary/20 transition-colors"
              role="listitem"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground text-sm truncate">{meeting.title}</h4>
                  {meeting.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {meeting.description}
                    </p>
                  )}
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  Pending
                </Badge>
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                <CalendarDays className="h-3 w-3" />
                {formatDate(meeting.dueDate ?? meeting.date)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default PendingMeetings;
